import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Seo from '../components/Seo'
import {
  createProjectFromTemplate, deleteProject, deleteSheet, exportBackup, genId,
  importBackup, listProjects, listSheets, saveProject, saveSheet,
} from '../lib/storage'
import type { BackupData, Project, SavedSheet } from '../lib/storage'

// Editable shape we hold in local state while the user is modifying a sheet.
// Keep qty as a string so the input can hold transient values like "" while
// typing. We coerce back to number on save.
interface EditForm {
  name: string
  summary: string
  lines: { label: string; qty: string; unit: string }[]
}
import { PROJECT_TEMPLATES, templateName, templateDesc, templateTaskName } from '../data/projectTemplates'
import { useLocale, localizedHref } from '../i18n/locale'
import { t } from '../i18n/strings'

export default function MySheets() {
  const navigate = useNavigate()
  const locale = useLocale()
  const tr = (k: string) => t(locale, k)
  const L = (en: string) => localizedHref(en, locale)
  const [sheets, setSheets] = useState<SavedSheet[]>([])
  const [projects, setProjects] = useState<Project[]>([])
  const [loaded, setLoaded] = useState(false)
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [templateId, setTemplateId] = useState('') // '' = blank project
  const [importMsg, setImportMsg] = useState('')
  // Which saved sheet currently has its "attach to project" picker open
  // inline. Null = no picker open. Toggling collapses any previously open one.
  const [attachingId, setAttachingId] = useState<string | null>(null)
  // Which saved sheet is currently being edited inline. At most one sheet may
  // be in either attach or edit mode at a time — opening one closes the other.
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState<EditForm>({ name: '', summary: '', lines: [] })
  const fileRef = useRef<HTMLInputElement>(null)

  const refresh = () =>
    Promise.all([listSheets(), listProjects()]).then(([s, p]) => {
      setSheets(s)
      setProjects(p)
      setLoaded(true)
    })

  useEffect(() => { refresh() }, [])

  const removeSheet = async (id: string) => {
    await deleteSheet(id)
    setSheets((s) => s.filter((x) => x.id !== id))
  }

  const createProject = async () => {
    const tpl = PROJECT_TEMPLATES.find((t) => t.id === templateId)
    const name = newName.trim() || (tpl ? templateName(tpl, locale) : '')
    if (!name) return
    let pid: string
    if (tpl) {
      pid = await createProjectFromTemplate(
        name,
        tpl.tasks.map((task) => ({
          name: templateTaskName(task, locale),
          category: task.category,
          calculatorType: task.calculatorType,
        })),
      )
    } else {
      const now = new Date().toISOString()
      const p: Project = { id: genId(), name, createdAt: now, updatedAt: now }
      await saveProject(p)
      pid = p.id
    }
    navigate(`${locale === 'fr' ? '/fr' : ''}/my-sheets/project/${pid}`)
  }

  const cancelCreate = () => { setCreating(false); setNewName(''); setTemplateId('') }

  const onExport = async () => {
    const data = await exportBackup()
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `renosheets-backup-${new Date().toISOString().slice(0, 10)}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  const onImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      try {
        const data = JSON.parse(await file.text()) as BackupData
        const n = await importBackup(data)
        await refresh()
        setImportMsg(
          locale === 'fr'
            ? `Importé ${n.projects} projet(s), ${n.tasks} tâche(s) et ${n.sheets} feuille(s).`
            : `Imported ${n.projects} project(s), ${n.tasks} task(s) and ${n.sheets} sheet(s).`,
        )
      } catch (err) {
        setImportMsg(err instanceof Error ? err.message : tr('mysheets.importError'))
      }
    }
    if (fileRef.current) fileRef.current.value = ''
  }

  // Enter inline edit mode for a sheet. Closes any open attach picker so the
  // two interactions can't fight each other for the same card.
  const openEdit = (s: SavedSheet) => {
    setAttachingId(null)
    setEditingId(s.id)
    setEditForm({
      name: s.name,
      summary: s.summary,
      lines: (s.lines ?? []).map((l) => ({
        label: l.label,
        qty: l.qty != null ? String(l.qty) : '',
        unit: l.unit ?? '',
      })),
    })
  }

  const cancelEdit = () => setEditingId(null)

  // Persist the inline edit back to storage. We coerce qty strings → numbers
  // and drop any lines whose label was cleared (so the user can effectively
  // delete a row by emptying its label).
  const saveEdit = async (original: SavedSheet) => {
    const cleanedLines = editForm.lines
      .filter((l) => l.label.trim() !== '')
      .map((l) => ({
        label: l.label.trim(),
        qty: l.qty.trim() === '' ? undefined : parseFloat(l.qty) || 0,
        unit: l.unit.trim() || undefined,
      }))
    const updated: SavedSheet = {
      ...original,
      name: editForm.name.trim() || original.name,
      summary: editForm.summary.trim(),
      lines: cleanedLines.length > 0 ? cleanedLines : undefined,
    }
    await saveSheet(updated)
    setSheets((prev) => prev.map((x) => (x.id === original.id ? updated : x)))
    setEditingId(null)
  }

  // Click-through helper: stash the sheet id in sessionStorage and navigate
  // to the chosen project. The project board picks the flag up on mount and
  // auto-opens the Add Task form with that sheet pre-imported, so the user
  // lands one click away from saving the new card.
  const attachSheetToProject = (sheetId: string, projectId: string) => {
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem('rs-pending-attach', sheetId)
    }
    navigate(`${locale === 'fr' ? '/fr' : ''}/my-sheets/project/${projectId}`)
  }

  const removeProject = async (id: string) => {
    if (!confirm(tr('mysheets.deleteProject'))) return
    await deleteProject(id)
    setProjects((p) => p.filter((x) => x.id !== id))
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <Seo
        title={tr('mysheets.title')}
        description={tr('mysheets.desc')}
        path="/my-sheets/"
      />
      <h1 className="text-3xl">{tr('mysheets.h1')}</h1>
      <p className="mt-1 text-text-muted">
        {tr('mysheets.introPre')}
        <Link to={L('/kanban-board/')} className="text-brand-text underline">{tr('mysheets.introLink')}</Link>
        {tr('mysheets.introPost')}
      </p>

      {/* Projects */}
      <section className="mt-8">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-text">{tr('mysheets.projects')}</h2>
          {!creating && (
            <button
              onClick={() => setCreating(true)}
              className="rounded-lg border border-brand px-3 py-1.5 text-sm font-medium text-brand-text hover:bg-brand/10"
            >
              {tr('mysheets.newProject')}
            </button>
          )}
        </div>

        {creating && (
          <div className="mt-3 rounded-xl border border-border bg-surface p-4">
            <div className="flex gap-2">
              <input
                autoFocus
                type="text"
                placeholder={(() => {
                  const tpl = PROJECT_TEMPLATES.find((t) => t.id === templateId)
                  return tpl ? templateName(tpl, locale) : tr('mysheets.projectName')
                })()}
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') createProject()
                  if (e.key === 'Escape') cancelCreate()
                }}
                className="flex-1 rounded-lg border border-border bg-surface-2 px-3 py-2 text-sm text-text placeholder:text-text-subtle focus:outline-none focus:ring-1 focus:ring-brand"
              />
              <button
                onClick={createProject}
                disabled={!newName.trim() && !templateId}
                className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-40"
              >
                {tr('mysheets.create')}
              </button>
              <button
                onClick={cancelCreate}
                className="rounded-lg border border-border px-3 py-2 text-sm text-text-muted hover:bg-surface-2"
              >
                {tr('mysheets.cancel')}
              </button>
            </div>

            <p className="mt-4 mb-2 text-xs font-semibold uppercase tracking-wide text-text-subtle">{tr('mysheets.startFromTemplate')}</p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              <button
                type="button"
                onClick={() => setTemplateId('')}
                className={`rounded-lg border p-3 text-left transition-colors ${templateId === '' ? 'border-brand bg-brand-tint/40' : 'border-border bg-surface-2 hover:border-border-strong'}`}
              >
                <div className="text-xl">📝</div>
                <div className="mt-1 text-sm font-medium text-text">{tr('mysheets.blankProject')}</div>
                <div className="text-xs text-text-subtle">{tr('mysheets.startEmpty')}</div>
              </button>
              {PROJECT_TEMPLATES.map((tpl) => (
                <button
                  key={tpl.id}
                  type="button"
                  onClick={() => setTemplateId(tpl.id)}
                  className={`rounded-lg border p-3 text-left transition-colors ${templateId === tpl.id ? 'border-brand bg-brand-tint/40' : 'border-border bg-surface-2 hover:border-border-strong'}`}
                >
                  <div className="text-xl">{tpl.icon}</div>
                  <div className="mt-1 text-sm font-medium text-text">{templateName(tpl, locale)}</div>
                  <div className="text-xs text-text-subtle">{tpl.tasks.length} {tr('mysheets.tasksWord')} · {templateDesc(tpl, locale)}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {loaded && projects.length === 0 && !creating && (
          <p className="mt-4 rounded-lg border border-dashed border-border bg-surface-2 p-6 text-center text-text-subtle">
            {tr('mysheets.noProjects')}
          </p>
        )}

        <ul className="mt-3 space-y-2">
          {projects.map((p) => (
            <li key={p.id} className="flex items-center gap-3 rounded-lg border border-border bg-surface p-4">
              <button
                onClick={() => navigate(`${locale === 'fr' ? '/fr' : ''}/my-sheets/project/${p.id}`)}
                className="min-w-0 flex-1 text-left"
              >
                <div className="font-semibold text-text hover:text-brand-text">{p.name}</div>
                <div className="text-xs text-text-subtle">{tr('mysheets.updated')} {new Date(p.updatedAt).toLocaleDateString(locale === 'fr' ? 'fr-CA' : 'en-US')}</div>
              </button>
              <button onClick={() => removeProject(p.id)} className="text-sm text-danger hover:underline">
                {tr('mysheets.delete')}
              </button>
            </li>
          ))}
        </ul>
      </section>

      {/* Saved Sheets */}
      <section className="mt-10">
        <h2 className="text-xl font-semibold text-text">{tr('mysheets.savedSheets')}</h2>
        <p className="mt-1 text-sm text-text-muted">{tr('mysheets.savedSheetsSub')}</p>

        {loaded && sheets.length === 0 && (
          <p className="mt-4 rounded-lg border border-dashed border-border bg-surface-2 p-6 text-center text-text-subtle">
            {tr('mysheets.noSheets')}
          </p>
        )}

        {loaded && sheets.length > 0 && (
          <p className="mt-2 text-xs text-text-subtle">{tr('mysheets.editHint')}</p>
        )}

        <ul className="mt-3 space-y-2">
          {sheets.map((s) => {
            const isAttaching = attachingId === s.id
            const isEditing = editingId === s.id
            return (
              <li key={s.id} className="rounded-lg border border-border bg-surface p-4">
                <div className="flex items-start justify-between gap-3">
                  {/* Click the body to edit. The button wrapper gives the area
                      keyboard focus + a hover affordance, signaling it's
                      interactive (since "click to edit" isn't obvious otherwise). */}
                  <button
                    type="button"
                    onClick={() => (isEditing ? cancelEdit() : openEdit(s))}
                    className="min-w-0 flex-1 cursor-pointer rounded-md text-left -m-1 p-1 hover:bg-surface-2"
                    title={isEditing ? 'Close editor' : 'Edit'}
                  >
                    <div className="font-semibold text-text">{s.name}</div>
                    <div className="text-sm text-text-muted">{s.summary}</div>
                    <div className="text-xs text-text-subtle">{new Date(s.createdAt).toLocaleDateString(locale === 'fr' ? 'fr-CA' : 'en-US')}</div>
                  </button>
                  <div className="flex flex-shrink-0 flex-col items-end gap-1">
                    <button
                      onClick={() => {
                        if (isEditing) setEditingId(null)
                        setAttachingId(isAttaching ? null : s.id)
                      }}
                      className="rounded-md border border-brand px-3 py-1 text-xs font-medium text-brand-text hover:bg-brand/10"
                    >
                      {isAttaching ? tr('mysheets.attachCancel') : tr('mysheets.attach')}
                    </button>
                    <button onClick={() => removeSheet(s.id)} className="text-xs text-danger hover:underline">
                      {tr('mysheets.remove')}
                    </button>
                  </div>
                </div>

                {/* Inline edit form: rename, edit summary, tweak any line's
                    label/qty/unit. Emptying a row's label deletes that row on save. */}
                {isEditing && (
                  <div className="mt-3 space-y-3 border-t border-border pt-3">
                    <label className="block">
                      <span className="text-xs font-semibold uppercase tracking-wide text-text-subtle">{tr('mysheets.editName')}</span>
                      <input
                        type="text"
                        value={editForm.name}
                        onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                        className="mt-1 w-full rounded border border-border bg-surface-2 px-2 py-1.5 text-sm text-text focus:outline-none focus:ring-1 focus:ring-brand"
                      />
                    </label>
                    <label className="block">
                      <span className="text-xs font-semibold uppercase tracking-wide text-text-subtle">{tr('mysheets.editSummary')}</span>
                      <input
                        type="text"
                        value={editForm.summary}
                        onChange={(e) => setEditForm((f) => ({ ...f, summary: e.target.value }))}
                        className="mt-1 w-full rounded border border-border bg-surface-2 px-2 py-1.5 text-sm text-text focus:outline-none focus:ring-1 focus:ring-brand"
                      />
                    </label>

                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-text-subtle">{tr('mysheets.editLines')}</p>
                      {editForm.lines.length > 0 && (
                        <>
                          {/* 4-column grid: label · qty · unit · delete-×.
                              Header row labels the first three; the × column
                              is intentionally unlabelled. */}
                          <div className="mt-1 grid grid-cols-[1fr_70px_70px_24px] gap-1.5 text-[10px] font-medium uppercase tracking-wide text-text-subtle">
                            <span>{tr('mysheets.editLabel')}</span>
                            <span className="text-right">{tr('mysheets.editQty')}</span>
                            <span>{tr('mysheets.editUnit')}</span>
                            <span aria-hidden></span>
                          </div>
                          <ul className="mt-1 space-y-1">
                            {editForm.lines.map((line, idx) => (
                              <li key={idx} className="grid grid-cols-[1fr_70px_70px_24px] items-center gap-1.5">
                                <input
                                  type="text"
                                  value={line.label}
                                  onChange={(e) => setEditForm((f) => ({
                                    ...f,
                                    lines: f.lines.map((x, i) => (i === idx ? { ...x, label: e.target.value } : x)),
                                  }))}
                                  className="rounded border border-border bg-surface-2 px-2 py-1 text-sm text-text focus:outline-none focus:ring-1 focus:ring-brand"
                                />
                                <input
                                  type="number"
                                  inputMode="decimal"
                                  value={line.qty}
                                  onChange={(e) => setEditForm((f) => ({
                                    ...f,
                                    lines: f.lines.map((x, i) => (i === idx ? { ...x, qty: e.target.value } : x)),
                                  }))}
                                  className="rounded border border-border bg-surface-2 px-2 py-1 text-right text-sm text-text focus:outline-none focus:ring-1 focus:ring-brand"
                                />
                                <input
                                  type="text"
                                  value={line.unit}
                                  onChange={(e) => setEditForm((f) => ({
                                    ...f,
                                    lines: f.lines.map((x, i) => (i === idx ? { ...x, unit: e.target.value } : x)),
                                  }))}
                                  className="rounded border border-border bg-surface-2 px-2 py-1 text-sm text-text focus:outline-none focus:ring-1 focus:ring-brand"
                                />
                                {/* Per-line delete. Drops the line immediately
                                    from edit state (only persisted on Save). */}
                                <button
                                  type="button"
                                  onClick={() => setEditForm((f) => ({
                                    ...f,
                                    lines: f.lines.filter((_, i) => i !== idx),
                                  }))}
                                  aria-label={tr('mysheets.editDeleteLine')}
                                  title={tr('mysheets.editDeleteLine')}
                                  className="flex h-6 w-6 items-center justify-center rounded text-sm text-danger hover:bg-danger/10"
                                >
                                  ×
                                </button>
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                      {/* + Add line — for restoring something that was deleted
                          or adding a custom item to the buy list. */}
                      <button
                        type="button"
                        onClick={() => setEditForm((f) => ({
                          ...f,
                          lines: [...f.lines, { label: '', qty: '', unit: '' }],
                        }))}
                        className="mt-2 w-full rounded-md border border-dashed border-border py-1.5 text-xs font-medium text-brand-text hover:bg-surface-2"
                      >
                        {tr('mysheets.editAddLine')}
                      </button>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => saveEdit(s)}
                        className="rounded bg-brand px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
                      >
                        {tr('mysheets.editSave')}
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="rounded border border-border px-3 py-1.5 text-sm text-text-muted hover:bg-surface-2"
                      >
                        {tr('mysheets.cancel')}
                      </button>
                    </div>
                  </div>
                )}

                {/* Inline attach picker: lists existing projects. Click any one
                    to land in that project's board with the sheet pre-attached. */}
                {isAttaching && (
                  <div className="mt-3 border-t border-border pt-3">
                    {projects.length === 0 ? (
                      <p className="rounded-md bg-surface-2 p-3 text-sm text-text-muted">
                        {tr('mysheets.attachNoProjects')}
                      </p>
                    ) : (
                      <>
                        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-subtle">
                          {tr('mysheets.attachPick')}
                        </p>
                        <ul className="space-y-1">
                          {projects.map((p) => (
                            <li key={p.id}>
                              <button
                                onClick={() => attachSheetToProject(s.id, p.id)}
                                className="flex w-full items-center justify-between rounded-md border border-border bg-surface-2 px-3 py-2 text-left text-sm hover:border-brand hover:bg-brand/5"
                              >
                                <span className="font-medium text-text">{p.name}</span>
                                <span className="text-xs text-brand-text">→</span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      </section>

      {/* Backup & restore */}
      <section className="mt-10 rounded-xl border border-border bg-surface-2 p-5">
        <h2 className="flex items-center gap-2 text-xl font-semibold text-text">
          {tr('mysheets.backupH2')}
          {/* Subtle ⚠ flag — gives the section a "pay attention here" cue
              without using a full coloured alert box below the body copy. */}
          <span aria-hidden className="text-base text-amber-500">⚠</span>
        </h2>
        {/* Three stacked lines: the privacy framing, the red bold "what wipes
            it" warning on its own line for maximum readability, then the
            Export prompt that solves it. */}
        <p className="mt-1 text-sm text-text-muted">{tr('mysheets.backupBodyPre')}</p>
        <p className="mt-2 text-sm font-semibold text-danger">{tr('mysheets.backupBodyBold')}</p>
        <p className="mt-2 text-sm text-text-muted">{tr('mysheets.backupBodyPost')}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            onClick={onExport}
            className="rounded-lg border border-brand px-4 py-2 text-sm font-medium text-brand-text hover:bg-brand/10"
          >
            {tr('mysheets.exportBackup')}
          </button>
          <button
            onClick={() => fileRef.current?.click()}
            className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-text-muted hover:bg-surface"
          >
            {tr('mysheets.importBackup')}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="application/json,.json"
            onChange={onImportFile}
            className="hidden"
          />
        </div>
        {importMsg && <p className="mt-3 text-sm text-brand-text">{importMsg}</p>}
        {/* Tells the user where the downloaded backup will land + how to
            recognize it. The Import note follows so both buttons get one
            small explanatory line each. Use text-text-body (darker than
            text-subtle) — these are short, important notes, not fine print. */}
        <p className="mt-3 text-xs text-text-body">
          {tr('mysheets.exportNote')}
        </p>
        <p className="mt-1 text-xs text-text-body">
          {tr('mysheets.backupMergeNote')}
        </p>
      </section>
    </div>
  )
}
